import {
  metaCacheLookup,
  type CacheEntry,
  type MetaCachePolicy,
  type MetaCacheState,
} from '../cache/metaCache.ts';
import { instanceAssetPath } from '../instance/assets.ts';
import type { InstanceConfig } from '../instance/config.ts';
import type { ChannelDetails } from '../model/channel.ts';
import { pickImage } from '../model/media.ts';
import type { SubscriptionRef } from '../parse/subscriptions.ts';

/**
 * §8's lazy hydration, for the list `/api/v1/auth/subscriptions` returns.
 *
 * The endpoint gives `{author, authorId}` per row and nothing else, so a subscription list can render
 * its names immediately and needs one request per *avatar*. That request is
 * `/api/v1/channels/{ucid}` — **48–52 KB** measured on three channels, un-trimmable (`?fields=` is
 * ignored; see `parse/subscriptions.ts`), and there is no UCID-addressable avatar to fall back on. So
 * a 30-subscription account is **~1.5 MB** to draw thirty 48-pixel circles, on every visit, and §8's
 * "hydrate text-first and lazily on scroll, cached" is a bandwidth decision rather than a nicety.
 *
 * The three rules that make it one, and all three are here rather than in the screen because a
 * Compose list has the identical problem:
 *
 * 1. **A row renders without its avatar.** Text first, always — never a spinner where a name belongs.
 * 2. **Only what is on screen is fetched**, and only what the cache does not already hold.
 * 3. **A stale hit renders and refreshes**, never blanks (R14).
 */

/** The schema version of a cached channel row. Bumped → the whole cache is dropped (R3/R5). */
export const CHANNEL_META_VERSION = 1;

/**
 * ~120 bytes per channel, and deliberately **not** the 50 KB record it came from — R14's rule, applied
 * to the other entity. What a subscription row can render is a name, an avatar and a subscriber count;
 * a description, a banner, a tab list and thirty `latestVideos` are what the other 49 KB is.
 */
export interface CachedChannelMeta extends CacheEntry {
  readonly name: string;
  /**
   * **Instance-relative** (`/ggpht/…`), never absolute — see {@link instanceAssetPath} for why that is
   * R14's "store no image URLs" honoured rather than bent. `null` for a channel whose record carried
   * no usable avatar, which is a real state: `authorThumbnails[].url` is sometimes protocol-relative
   * and sometimes absent.
   */
  readonly avatarPath: string | null;
  readonly subscriberCount: number | null;
  /**
   * The channel no longer exists on YouTube — deleted, or terminated (`isChannelGone`). A tombstone
   * rather than a dropped fetch, for the loop's sake: an uncached miss is re-fetched every time the
   * cache moves, and dead channels were re-asked without end. The TTL still applies, on
   * purpose — a terminated channel can come back (measured: one did), so the fact is re-checked at
   * the same cadence any stale row is.
   */
  readonly gone?: true;
}

/** The tombstone {@link channelMetaFrom} cannot build — there is no record to reduce. */
export function channelGoneMeta(channelId: string, nowEpochSec: number): CachedChannelMeta {
  return {
    id: channelId,
    // Empty, so the row keeps rendering the subscription list's own name (`subscriptionRows`).
    name: '',
    avatarPath: null,
    subscriberCount: null,
    gone: true,
    cachedAtEpochSec: nowEpochSec,
    lastSeenEpochSec: nowEpochSec,
  };
}

/** The cache as it is persisted. The platform serialises this and nothing else. */
export type ChannelMetaCache = MetaCacheState<CachedChannelMeta>;

/**
 * The avatar a subscription row actually draws at, and the reason this is not `pickImage`'s default.
 *
 * `authorThumbnails[]` is sorted **ascending** and declares 32, 48, 76, 100, 176 and 512 px. A row
 * draws a 40 px circle, so 76 covers it at 2× — asking for 176 "to be safe" fetches four times the
 * bytes for a circle nobody inspects, thirty times over. (2c's `AVATAR_MIN_WIDTH` is 176 because a
 * search *channel card* draws at 88.)
 */
const ROW_AVATAR_MIN_WIDTH = 76;

/** Reduce a fetched channel record to the row, dropping the 49 KB the row cannot use. */
export function channelMetaFrom(
  config: InstanceConfig,
  channel: ChannelDetails,
  nowEpochSec: number,
): CachedChannelMeta {
  const avatar = pickImage(channel.avatars, { minWidth: ROW_AVATAR_MIN_WIDTH });
  return {
    id: channel.channelId,
    name: channel.name,
    avatarPath: avatar === null ? null : instanceAssetPath(config, avatar.url),
    subscriberCount: channel.subscriberCount,
    cachedAtEpochSec: nowEpochSec,
    lastSeenEpochSec: nowEpochSec,
  };
}

/** One subscription, ready to render — hydrated or not, and the difference is visible in the type. */
export interface SubscriptionRow {
  readonly channelId: string;
  /**
   * The cached name when there is one, else the list's own. Both are real: the list endpoint carries
   * `author`, so a row is never nameless — which is what makes rule 1 above implementable at all.
   */
  readonly name: string;
  /** Still instance-relative. The platform resolves it against the current instance at render time. */
  readonly avatarPath: string | null;
  readonly subscriberCount: number | null;
  /** Whether a channel record has been folded in. Drives the placeholder, not a spinner. */
  readonly hydrated: boolean;
  /** The channel is deleted or terminated — the manage page marks the tile. */
  readonly gone: boolean;
}

export interface SubscriptionRowsResult {
  readonly rows: readonly SubscriptionRow[];
  /**
   * The ids worth fetching *now* — the visible misses, plus visible stale hits. Already deduplicated
   * and already intersected with `visible`, so a caller that fires one request per id is correct by
   * construction rather than by remembering to filter.
   */
  readonly fetch: readonly string[];
}

export interface SubscriptionRowsInput {
  readonly subscriptions: readonly SubscriptionRef[];
  readonly cache: ChannelMetaCache;
  /**
   * Which rows are on screen. **Empty means "nothing yet", not "everything"** — the opposite default
   * would make the first paint fetch the whole list, which is the exact failure this module exists to
   * prevent, wearing a lazy loader's name.
   */
  readonly visible: readonly string[];
  /** Requests already in flight, so a scroll that re-enters a row does not fetch it twice. */
  readonly inFlight?: readonly string[];
  readonly nowEpochSec: number;
  readonly policy?: MetaCachePolicy;
}

/**
 * Fold the cache into the list and say what to fetch.
 *
 * The order is the endpoint's, untouched: Invidious returns subscriptions alphabetically and a list
 * re-sorted by "hydrated first" would rearrange itself as the avatars arrive, which is the most
 * disorienting possible way to render progress.
 */
export function subscriptionRows(input: SubscriptionRowsInput): SubscriptionRowsResult {
  const ids = input.subscriptions.map((subscription) => subscription.channelId);
  const lookup = metaCacheLookup(input.cache, ids, input.nowEpochSec, input.policy);
  const byId = new Map(lookup.hits.map((hit) => [hit.id, hit]));

  const rows = input.subscriptions.map((subscription): SubscriptionRow => {
    const meta = byId.get(subscription.channelId);
    if (meta === undefined) {
      return {
        channelId: subscription.channelId,
        name: subscription.name,
        avatarPath: null,
        subscriberCount: null,
        hydrated: false,
        gone: false,
      };
    }
    return {
      channelId: subscription.channelId,
      // The cached name wins on a disagreement, because it came from the channel's own record while
      // the list's came from the subscription row — and an empty `author` is survivable there
      // (`parseSubscriptionList`), so a cached name is sometimes the only one.
      name: meta.name === '' ? subscription.name : meta.name,
      avatarPath: meta.avatarPath,
      subscriberCount: meta.subscriberCount,
      hydrated: true,
      gone: meta.gone === true,
    };
  });

  const visible = new Set(input.visible);
  const inFlight = new Set(input.inFlight ?? []);
  const wanted = new Set([...lookup.misses, ...lookup.stale.map((entry) => entry.id)]);

  return {
    rows,
    fetch: ids.filter((id) => wanted.has(id) && visible.has(id) && !inFlight.has(id)),
  };
}
